/** ITCSI platform: client companies and onboarding a new one (M01-06). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FileDown, LifeBuoy, Plus } from 'lucide-react';
import { downloadFile } from '../../lib/files';
import { dateTime } from '../../lib/format';
import { api, ApiError } from '../../lib/api';
import { shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';

interface ClientCompany {
  id: string;
  registeredName: string;
  tradeName: string;
  status: 'SETUP' | 'ACTIVE' | 'SUSPENDED';
  vatStatus: 'VAT' | 'NON_VAT';
  createdAt: string;
  users: number;
  isSandbox: boolean;
  mfaRequired: boolean;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function CompaniesPage(): JSX.Element {
  const companies = useQuery({ queryKey: ['platform-companies'], queryFn: () => api<ClientCompany[]>('/platform/companies') });
  const [creating, setCreating] = useState(false);
  const [supportFor, setSupportFor] = useState<ClientCompany | null>(null);
  return (
    <>
      <PageHeader
        title="Client Companies"
        description="Creating a company does not give ITCSI access to its books. The Owner you name controls who gets in."
        actions={<Button onClick={() => setCreating(true)}><Plus aria-hidden="true" className="h-4 w-4" /> New company</Button>}
      />
      <Card>
        {companies.isLoading && <Skeleton className="h-40 w-full" />}
        {companies.data?.length === 0 && <EmptyState title="No client companies yet" />}
        {companies.data && companies.data.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Company</Th>
                <Th>VAT</Th>
                <Th>Users</Th>
                <Th>Status</Th>
                <Th>Two-step sign-in</Th>
                <Th>Created</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {companies.data.map((c) => (
                <tr key={c.id}>
                  <Td><span className="font-medium">{c.registeredName}</span>{c.tradeName && <span className="block text-xs text-slate-500">{c.tradeName}</span>}</Td>
                  <Td>{c.vatStatus === 'VAT' ? 'VAT' : 'Non-VAT'}</Td>
                  <Td className="tabular-nums">{c.users}</Td>
                  <Td><StatusChip tone={c.status === 'ACTIVE' ? 'ok' : c.status === 'SETUP' ? 'info' : 'critical'}>{c.status === 'SETUP' ? 'Setting up' : c.status === 'ACTIVE' ? 'Active' : 'Suspended'}</StatusChip></Td>
                  <Td><MfaToggle company={c} /></Td>
                  <Td className="whitespace-nowrap text-slate-500">{shortDate(c.createdAt)}</Td>
                  <Td>
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setSupportFor(c)}><LifeBuoy aria-hidden="true" className="h-4 w-4" /> Support access</Button>
                      {c.status === 'SUSPENDED' && <Button size="sm" variant="ghost" onClick={() => void downloadFile(`/platform/companies/${c.id}/export`).catch((e: Error) => window.alert(e.message))}><FileDown aria-hidden="true" className="h-4 w-4" /> Export (A8)</Button>}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {creating && <NewCompanyDialog onClose={() => setCreating(false)} />}
      {supportFor && <SupportRequestDialog company={supportFor} onClose={() => setSupportFor(null)} />}
      <MySupportSessions />
    </>
  );
}

function NewCompanyDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    registeredName: '',
    tradeName: '',
    vatStatus: 'VAT',
    taxpayerClass: 'MICRO',
    fiscalYearStartMonth: '1',
    coaTemplate: 'SERVICE',
    ownerName: '',
    ownerEmail: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ ownerTemporaryPassword: string | null } | null>(null);
  const set = (patch: Partial<typeof form>): void => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () => api<{ companyId: string; ownerTemporaryPassword: string | null }>('/platform/companies', { method: 'POST', body: { ...form, fiscalYearStartMonth: Number(form.fiscalYearStartMonth) } }),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['platform-companies'] });
      setResult(r);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not create the company.'),
  });

  if (result) {
    return (
      <Modal title="Company created" onClose={onClose}>
        {result.ownerTemporaryPassword ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">Send the Owner this temporary password privately. They set their own password and turn on two-step sign-in at first sign-in.</p>
            <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-slate-100 px-4 py-3 font-mono text-lg dark:bg-slate-800">
              <span>{result.ownerTemporaryPassword}</span>
              <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(result.ownerTemporaryPassword!)}><Copy aria-hidden="true" className="h-4 w-4" /> Copy</Button>
            </div>
          </>
        ) : (
          <p className="text-sm">The Owner already has a CoreX-lite account; the new company appears in their company list.</p>
        )}
        <div className="mt-4 flex justify-end"><Button onClick={onClose}>Done</Button></div>
      </Modal>
    );
  }

  return (
    <Modal title="New client company" description="Roles, a head-office branch, the current fiscal year and a chart of accounts are set up automatically." onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <Field label="Registered name" required><input className={inputClass} value={form.registeredName} onChange={(e) => set({ registeredName: e.target.value })} autoFocus /></Field>
        <Field label="Trade name"><input className={inputClass} value={form.tradeName} onChange={(e) => set({ tradeName: e.target.value })} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="VAT registration">
            <select className={inputClass} value={form.vatStatus} onChange={(e) => set({ vatStatus: e.target.value })}>
              <option value="VAT">VAT-registered</option>
              <option value="NON_VAT">Non-VAT</option>
            </select>
          </Field>
          <Field label="Taxpayer class">
            <select className={inputClass} value={form.taxpayerClass} onChange={(e) => set({ taxpayerClass: e.target.value })}>
              <option value="MICRO">Micro</option>
              <option value="SMALL">Small</option>
              <option value="MEDIUM">Medium</option>
              <option value="LARGE">Large</option>
            </select>
          </Field>
          <Field label="Fiscal year starts">
            <select className={inputClass} value={form.fiscalYearStartMonth} onChange={(e) => set({ fiscalYearStartMonth: e.target.value })}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}{i === 0 ? ' (calendar year)' : ''}</option>)}
            </select>
          </Field>
          <Field label="Chart of accounts">
            <select className={inputClass} value={form.coaTemplate} onChange={(e) => set({ coaTemplate: e.target.value })}>
              <option value="SERVICE">PH Service SME</option>
              <option value="TRADING">PH Trading SME</option>
              <option value="BLANK">Blank (essentials only)</option>
            </select>
          </Field>
          <Field label="Owner's name" required><input className={inputClass} value={form.ownerName} onChange={(e) => set({ ownerName: e.target.value })} /></Field>
          <Field label="Owner's email" required><input className={inputClass} type="email" value={form.ownerEmail} onChange={(e) => set({ ownerEmail: e.target.value })} /></Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Create company</Button>
        </div>
      </form>
    </Modal>
  );
}

/** M02-07: ITCSI asks; the company's owner decides. */
function SupportRequestDialog({ company, onClose }: { company: ClientCompany; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(2);
  const send = useMutation({
    mutationFn: () => api(`/platform/companies/${company.id}/support-sessions`, { method: 'POST', body: { reason, hours } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['my-support'] }); onClose(); },
  });
  return (
    <Modal title={`Request support access to ${company.registeredName}`} description="The owner sees your reason and approves a read-only, time-limited session. Every page you open is logged for them." onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
        {send.error && <Alert tone="critical">{(send.error as Error).message}</Alert>}
        <Field label="Reason" required hint="At least 10 characters; the client reads this."><textarea className={inputClass} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} required minLength={10} /></Field>
        <Field label="Hours needed"><select className={inputClass} value={hours} onChange={(e) => setHours(Number(e.target.value))}>{[1, 2, 4, 8, 24].map((h) => <option key={h} value={h}>{h}</option>)}</select></Field>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" busy={send.isPending}>Send request</Button></div>
      </form>
    </Modal>
  );
}

function MySupportSessions(): JSX.Element | null {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['my-support'], queryFn: () => api<{ id: string; company: string; reason: string; status: string; endsAt: string | null; createdAt: string; requests: number }[]>('/platform/support-sessions') });
  const end = useMutation({ mutationFn: (id: string) => api(`/platform/support-sessions/${id}/end`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['my-support'] }) });
  if (!q.data?.length) return null;
  return (
    <Card title="My support sessions" description="Approved sessions appear in your company switcher until they end." className="mt-4">
      <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
        {q.data.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span><span className="font-medium">{s.company}</span> <span className="text-slate-500">· {s.reason}</span></span>
            <span className="flex items-center gap-2">
              <StatusChip tone={s.status === 'ACTIVE' ? 'info' : s.status === 'PENDING' ? 'warning' : 'neutral'}>{s.status.toLowerCase()}</StatusChip>
              <span className="text-xs text-slate-500">{s.endsAt ? `until ${dateTime(s.endsAt)}` : `asked ${dateTime(s.createdAt)}`}</span>
              {(s.status === 'ACTIVE' || s.status === 'PENDING') && <Button size="sm" variant="ghost" onClick={() => end.mutate(s.id)}>End</Button>}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Platform admins can waive two-step sign-in for test and demo companies only. */
function MfaToggle({ company }: { company: ClientCompany }): JSX.Element {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (required: boolean) => api(`/platform/companies/${company.id}/mfa`, { method: 'PATCH', body: { required, reason: required ? 'Restored' : 'Testing / demo company' } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform-companies'] }),
  });
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm" title={company.mfaRequired ? 'Required' : 'Waived for testing — users of only this company skip the code'}>
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={company.mfaRequired}
        disabled={set.isPending}
        onChange={(e) => {
          if (!e.target.checked && !window.confirm(`Turn off two-step sign-in for ${company.registeredName}? Use this only for test or demo companies. Platform admins and advisor staff keep it on.`)) return;
          set.mutate(e.target.checked);
        }}
      />
      {company.mfaRequired ? <span>Required</span> : <StatusChip tone="warning">Off (testing)</StatusChip>}
    </label>
  );
}
